import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { Observable, defer, from } from 'rxjs';

import { environment } from '../../../environments/environment';
import { BeneficiaryDependentUpdateDto } from '../../modules/beneficiaries/dtos/beneficiary-dependent-update';
import { BeneficiaryUpdateDto } from '../../modules/beneficiaries/dtos/beneficiary-update';
import { BeneficiaryStatusFilter } from '../../modules/beneficiaries/enums/beneficiary-status-filter.enum';
import { SnackBarComponent } from '../../shared/components/snack-bar/snack-bar.component';
import {
	OperationType,
	UploadStatusFilter,
} from '../../shared/enums/batch-actions.enum';
import { TemplateFileType } from '../../shared/enums/template-file.enum';

import {
	BatchActionFileResponse,
	BatchActionInfo,
	BatchActionsResponse,
} from './models/batch-actions.model';
import {
	BeneficiaryInfoResponse,
	BeneficiaryWalletResponse,
	CpfBeneficiarioResponse,
	CpfTitularResponse,
	DependentRegistration,
	EditBeneStatusRequest,
	EditBeneStatusResponse,
	OrgBeneficiaryListResponse,
	OrgBeneficiaryPendencies,
	OrgBeneficiaryPendenciesRequest,
	TitularRegistration,
} from './models/beneficiaries.model';
import {
	BenefitRequest,
	BenefitResponse,
	BenefitsResponse,
} from './models/benefits.model';
import { INotification } from './models/notification';
import {
	AvailabilityResponse,
	CNPJAvailability,
	CpfCheckResponse,
	DashBeneInfo,
	DashChartInfo,
	DashChartResponse,
	DashChartsUrlsResponse,
	GroupsListResponse,
	HeadquarterSummary,
	IdAndMessageResponse2,
	MessageResponse,
	NewOrganizationRequest,
	OrgHubItem,
	OrgListResponse,
	OrgRegistration,
	OrgUsersResponse,
	PartnerRegistration,
	PartnerRequest,
	URLResponse,
	UserEditRequest,
	UserRegistration,
	UserRequest,
	ViaCepResponse,
} from './models/organizations.model';
import {
	PolicyRegistrationResponse,
	PolicyRequest,
	PolicySummary,
} from './models/policies.model';
import { PositionRequest, PositionSummary } from './models/positions.model';
import { ProductsSummary } from './models/products.model';
import { ScoreResponse } from './models/score.model';

// TODO remove legacy actions/effects/endpoints/etc. after FULL transition to /v1/

/**
 * @deprecated Esta classe deverá ser removida em versões futuras.
 * Novas funcionalidades e melhorias de organizações devem ser criadas na classe OrganizationsService.
 * Recursos de outros módulos devem ser criados em suas respectivas classes de serviço
 */
@Injectable({
	providedIn: 'root',
})
export class OrganizationsLegacyService {
	apiUrlBenefits = `${environment.apiUrl}/benefits`;
	apiUrlOrg = `${environment.apiUrl}/organization`;
	apiUrlPartner = `${environment.apiUrl}/partner`;
	apiUrlPolicies = `${environment.apiUrl}/policy`;
	apiUrlPositions = `${environment.apiUrl}/position`;
	apiUrlProducts = `${environment.apiUrl}/products`;

	constructor(
		private readonly http: HttpClient,
		private readonly _snackBar: MatSnackBar,
	) {}

	/**
	 * Check CPF dependente
	 * @param orgId string
	 * @param cpf string
	 */
	checkCPFDependente(orgId: string, cpf: string): Observable<CpfCheckResponse> {
		const url = `${environment.apiUrl}/dependent/has-dependent/${orgId}`;

		return this.http.post<CpfCheckResponse>(url, { cpf });
	}

	/**
	 * Check CPF titular
	 * @param orgId string
	 * @param cpf string
	 */
	checkCPFTitular(orgId: string, cpf: string): Observable<CpfTitularResponse> {
		const url = `${environment.apiUrl}/dependent/has-beneficiary/${orgId}`;

		return this.http.post<CpfTitularResponse>(url, { cpf });
	}

	/**
	 * Check CPF dependente
	 * @param orgId string
	 * @param email string
	 */
	checkEmailDependente(
		orgId: string,
		email: string,
	): Observable<CpfCheckResponse> {
		const url = `${environment.apiUrl}/dependent/has-email`;

		return this.http.post<CpfCheckResponse>(url, { email });
	}

	/**
	 * Delete Beneficiary File
	 * @param fileId string
	 * @param inactive boolean
	 */
	deleteBeneficiaryFile(fileId: string): Observable<MessageResponse> {
		let url = `${environment.apiUrl}/beneficiary/archives/${fileId}`;

		return this.http.delete<MessageResponse>(url);
	}

	/**
	 * Delete Benefit
	 * @param orgId string
	 * @param id string
	 */
	deleteBenefit(orgId: string, id: string): Observable<MessageResponse> {
		const url = `${environment.apiUrl}/benefits/${orgId}/${id}`;

		return this.http.delete<MessageResponse>(url);
	}

	/**
	 * Delete Dependent
	 * @param orgId string
	 * @param depId string
	 * @param cpf string
	 */
	deleteDependent(
		orgId: string,
		depId: string,
		cpf: string,
	): Observable<MessageResponse> {
		const url = `${environment.apiUrl}/beneficiary/v1/${orgId}/${depId}/retired-and-inactive`;

		return this.http.post<MessageResponse>(url, { cpf });
	}

	/**
	 * Delete Beneficiary Notification
	 * @param id string
	 */
	deleteNotificationByBeneficiary(id: string): Observable<void> {
		const url = `${environment.apiUrl}/beneficiary/notifications/${id}`;

		return this.http.patch<void>(url, {});
	}

	/**
	 * Delete Beneficiary registration pendency
	 * @param pendencyId string
	 */
	deleteOrgBenePendency(pendencyId: string): Observable<unknown> {
		const url = `${environment.apiUrl}/beneficiary/pendency/${pendencyId}`;

		return this.http.delete<unknown>(url);
	}

	/**
	 * Delete Policy
	 * @param orgId string
	 * @param id string
	 */
	deletePolicy(orgId: string, id: string): Observable<MessageResponse> {
		const url = `${environment.apiUrl}/policy/${orgId}/${id}`;

		return this.http.delete<MessageResponse>(url);
	}

	/**
	 * Delete Position
	 * @param orgId string
	 * @param id string
	 */
	deletePosition(orgId: string, id: string): Observable<MessageResponse> {
		const url = `${environment.apiUrl}/position/${id}`;

		return this.http.delete<MessageResponse>(url);
	}

	/**
	 * Search Address by CEP
	 * @param cep string '99999999'
	 */
	getAddress(cep: string): Observable<ViaCepResponse> {
		const url = `https://viacep.com.br/ws/${cep}/json/`;

		return this.http.get<ViaCepResponse>(url);
	}

	/**
	 * Download batch action file
	 * @param url string
	 */
	getBatchActionFile(url: string) {
		const req = this.http
			.get(url, { responseType: 'blob' as 'json' })
			.toPromise();

		return defer(() => from(req));
	}

	/**
	 * Get batch action file download url
	 * @param key string
	 */
	getBatchActionFileDownloadUrl(
		key: string,
	): Observable<BatchActionFileResponse> {
		const url = `${environment.apiUrl}/upload/xls/download/${key}`;

		return this.http.post<BatchActionFileResponse>(url, {});
	}

	/**
	 * Get batch action detailed information
	 * @param id string
	 */
	getBatchActionInfo(id: string): Observable<BatchActionInfo> {
		const url = `${environment.apiUrl}/upload/xls/description/${id}`;

		return this.http.get<BatchActionInfo>(url);
	}

	/**
	 * Get batch actions
	 * @param orgId string
	 */
	getBatchActions(
		orgId: string,
		status?: UploadStatusFilter,
		operationType?: OperationType,
	): Observable<BatchActionsResponse> {
		const url = `${environment.apiUrl}/upload/xls/${orgId}`;

		let params = new HttpParams();

		if (status && status !== UploadStatusFilter.ALL) {
			params = params.set('status', status);
		}

		if (operationType) {
			params = params.set('type', operationType);
		}

		return this.http.get<BatchActionsResponse>(url, { params });
	}

	/**
	 * Get org beneficiaries
	 * @param orgId string
	 * @param status string = BeneficiaryStatusFilter
	 */
	getBeneficiaries(
		orgId: string,
		status: BeneficiaryStatusFilter,
		sort: string = 'asc',
		term: string = '',
	): Observable<OrgBeneficiaryListResponse> {
		let url = `${environment.apiUrl}/beneficiary/v1/${orgId}/${status}?order=${sort}&search=${term}`;

		if (
			status === BeneficiaryStatusFilter.EXCLUSION ||
			status === BeneficiaryStatusFilter.INACTIVE
		) {
			url = `${environment.apiUrl}/beneficiary/${orgId}/${status}?order=${sort}&search=${term}`;
		}

		return this.http.get<OrgBeneficiaryListResponse>(url);
	}

	/**
	 * Get beneficiary registration (only from beneficiary page)
	 * @param orgId string
	 * @param beneficiaryId string
	 */
	getBeneficiary(
		orgId: string,
		beneficiaryId: string,
	): Observable<BeneficiaryUpdateDto> {
		const url = `${environment.apiUrl}/beneficiary/edit/${orgId}/${beneficiaryId}`;

		return this.http.get<BeneficiaryUpdateDto>(url);
	}

	/**
	 * Fetch Beneficiary All Policies
	 * deprecated
	 * @param orgId string
	 * @param beneficiaryId string
	 * deprecated
	 */
	getBeneficiaryAllPolicies(
		orgId: string,
		beneficiaryId: string,
	): Observable<BeneficiaryInfoResponse> {
		const url = `${environment.apiUrl}/beneficiary/policy/all/${beneficiaryId}`;

		return this.http.get<BeneficiaryInfoResponse>(url);
	}

	/**
	 * Fetch All Beneficiary Benefits
	 * @param orgId string
	 * @param positionId string
	 */
	getBeneficiaryBenefits(
		orgId: string,
		positionId: string,
	): Observable<BeneficiaryInfoResponse> {
		const url = `${environment.apiUrl}/beneficiary/v1/${orgId}/${positionId}/benefits`;

		return this.http.get<BeneficiaryInfoResponse>(url);
	}

	/**
	 * Get Beneficiary File Download URL
	 * @param fileId string
	 */
	getBeneficiaryFileURL(fileId: string): Observable<{ url: string }> {
		let url = `${environment.apiUrl}/beneficiary/archives/download/${fileId}`;

		return this.http.get<{ url: string }>(url);
	}

	/**
	 * Fetch All Beneficiary Policies
	 * @param orgId string
	 * @param positionId string
	 */
	getBeneficiaryPolicies(
		orgId: string,
		positionId: string,
	): Observable<BeneficiaryInfoResponse> {
		const url = `${environment.apiUrl}/beneficiary/v1/${orgId}/${positionId}/policies`;

		return this.http.get<BeneficiaryInfoResponse>(url);
	}

	/**
	 * Fetch Beneficiary View
	 * @param orgId string
	 * @param beneficiaryType string
	 * @param beneficiaryId string
	 */
	getBeneficiaryView(
		orgId: string,
		beneficiaryType: string,
		beneficiaryId: string,
	): Observable<TitularRegistration> {
		const ir =
			beneficiaryType === 'inativo'
				? '/inactive'
				: beneficiaryType === 'aposentado'
					? '/retired'
					: '';
		const url = `${environment.apiUrl}/beneficiary/v1/${orgId}${ir}/${beneficiaryId}`;

		return this.http.get<TitularRegistration>(url);
	}

	/**
	 * Fetch Beneficiary Wallets
	 * @param orgId string
	 * @param beneficiaryId string
	 * deprecated
	 */
	getBeneficiaryWallets(
		orgId: string,
		beneficiaryId: string,
	): Observable<BeneficiaryWalletResponse> {
		const url = `${environment.apiUrl}/beneficiary/wallets/${beneficiaryId}`;

		return this.http.get<BeneficiaryWalletResponse>(url);
	}

	/**
	 * Get benefit registration
	 * @param orgId string
	 * @param benefitId string
	 */
	getBenefit(orgId: string, benefitId: string): Observable<BenefitResponse> {
		const url = `${this.apiUrlBenefits}/${orgId}/${benefitId}`;

		return this.http.get<BenefitResponse>(url);
	}

	/**
	 * Get benefits
	 * @param orgId string
	 */
	getBenefits(orgId: string): Observable<BenefitsResponse> {
		const url = `${this.apiUrlBenefits}/${orgId}`;

		return this.http.get<BenefitsResponse>(url);
	}

	/**
	 * Get Bulk action template file url
	 * @param actionType OrgActionType
	 * @param organizationId string
	 */
	getBulkActionTemplateFileURL(
		type: TemplateFileType,
		organizationId: string,
	): Observable<URLResponse> {
		const url = `${environment.apiUrl}/file-manager/templates?organizationId=${organizationId}&action=${type}`;

		return this.http.get<URLResponse>(url);
	}

	/**
	 * Check CNPJ availability
	 * @param category string 'organization' | 'partner'
	 * @param cnpj string
	 */
	getCNPJAvailability(
		category: string,
		cnpj: string,
	): Observable<CNPJAvailability> {
		const url = `${this.apiUrlOrg}/verification/${category}/${cnpj}`;

		return this.http.get<CNPJAvailability>(url);
	}

	/**
	 * Check CPF availability
	 * @param cpf string
	 */
	getCPFAvailability(cpf: string): Observable<AvailabilityResponse> {
		const url = `${environment.apiUrl}/user/v1/cpf-validator`;

		return this.http.post<AvailabilityResponse>(url, { cpf });
	}

	/**
	 * Check CPF Beneficiário
	 * @param orgId string
	 * @param cpf string
	 */
	getCPFBeneficiario(
		orgId: string,
		cpf: string,
	): Observable<CpfBeneficiarioResponse> {
		const url = `${environment.apiUrl}/beneficiary/v1/${orgId}/type`;

		return this.http.post<CpfBeneficiarioResponse>(url, { cpf });
	}

	/**
	 * Search Cities by UF id
	 * @param id number
	 */
	getCities(id: number): Observable<any[]> {
		const url = `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${id}/municipios`;

		return this.http.get<any[]>(url);
	}

	/**
	 * Get Dashboard beneficiaries info
	 * @param orgId string
	 */
	getDashBeneInfo(orgId: string): Observable<DashBeneInfo> {
		const url = `${environment.apiUrl}/dashboard/v1/${orgId}`;

		return this.http.get<DashBeneInfo>(url);
	}

	/**
	 * Get Dashboard charts
	 * @param orgId string
	 */
	getDashCharts(orgId: string): Observable<DashChartResponse> {
		const url = `${environment.apiUrl}/dashboard/v1/report/${orgId}`;

		return this.http.get<DashChartResponse>(url);
	}

	/**
	 * Get Dashboard charts URLs
	 */
	getDashChartsUrls(): Observable<DashChartsUrlsResponse> {
		const url = `${environment.apiUrl}/dashboard/v1/report/links`;

		return this.http.get<DashChartsUrlsResponse>(url);
	}

	/**
	 * Get Dependent
	 * @param orgId string
	 * @param depId string
	 */
	getDependent(
		orgId: string,
		depId: string,
	): Observable<DependentRegistration> {
		const url = `${environment.apiUrl}/beneficiary/v1/${orgId}/${depId}`;

		return this.http.get<DependentRegistration>(url);
	}

	/**
	 * Fetch Dependent Benefits
	 * @param titularId string
	 */
	getDependentBenefits(titularId: string): Observable<BeneficiaryInfoResponse> {
		const url = `${environment.apiUrl}/beneficiary/v1/${titularId}/dependent/benefits`;

		return this.http.get<BeneficiaryInfoResponse>(url);
	}

	/**
	 * Fetch Dependent Wallets
	 * @param dependentId string
	 * deprecated
	 */
	getDependentWallets(
		dependentId: string,
	): Observable<BeneficiaryWalletResponse> {
		const url = `${environment.apiUrl}/dependent/benefits/${dependentId}`;

		return this.http.get<BeneficiaryWalletResponse>(url);
	}

	/**
	 * Check Email availability
	 * @param email string
	 */
	getEmailAvailability(email: string): Observable<AvailabilityResponse> {
		const url = `${environment.apiUrl}/user/v1/email-validator`;

		return this.http.post<AvailabilityResponse>(url, { email });
	}

	/**
	 * Get groups list
	 * @param orgId string
	 */
	getGroupsList(orgId: string): Observable<GroupsListResponse> {
		const url = `${environment.apiUrl}/groups/by-user/${orgId}`;

		return this.http.get<GroupsListResponse>(url);
	}

	/**
	 * Fetch non-filial organization list
	 */
	getHeadquarterOrgs(): Observable<HeadquarterSummary[]> {
		const url = `${this.apiUrlOrg}/main`;

		return this.http.get<HeadquarterSummary[]>(url);
	}

	/**
	 * Fetch Beneficiary Notifications
	 * @param beneficiaryId string
	 * deprecated
	 */
	getNotificationByBeneficiary(
		beneficiaryId: string,
	): Observable<{ messages: INotification[] }> {
		const url = `${environment.apiUrl}/beneficiary/notifications/${beneficiaryId}`;

		return this.http.get<{ messages: INotification[] }>(url);
	}

	/**
	 * Fetch Organization Beneficiaries XLSX URL
	 * @param orgId string
	 */
	getOrgBeneDataURL(
		org_id: string,
		isMatriz?: boolean,
	): Observable<URLResponse> {
		const url = `${environment.apiUrl}/beneficiary/v1/report-beneficiary`;

		return this.http.post<URLResponse>(url, { isMatriz, org_id });
	}

	/**
	 * Fetch Organization Beneficiaries Inactive XLSX URL
	 * @param orgId string
	 */
	getOrgBeneInactiveDataURL(
		org_id: string,
		isMatriz?: boolean,
	): Observable<URLResponse> {
		const url = `${environment.apiUrl}/beneficiary/report/inactive-beneficiaries`;

		return this.http.post<URLResponse>(url, { isMatriz, org_id });
	}

	/**
	 * Fetch Beneficiary registration status/pendencies
	 * @param beneficiaryId string
	 */
	getOrgBenePendencies(
		beneficiaryId: string,
	): Observable<OrgBeneficiaryPendencies> {
		const url = `${environment.apiUrl}/beneficiary/pendency/${beneficiaryId}`;

		return this.http.get<OrgBeneficiaryPendencies>(url);
	}

	/**
	 * Fetch org hub
	 * @param id string
	 */
	getOrgHub(id: string): Observable<OrgHubItem[]> {
		const url = `${this.apiUrlOrg}/hub/${id}`;

		return this.http.get<OrgHubItem[]>(url);
	}

	/**
	 * Get org users
	 * @param orgId string
	 * @param status string = 'active'|'inactive'
	 */
	getOrgUsers(
		orgId: string,
		status: 'active' | 'inactive',
	): Observable<OrgUsersResponse> {
		const url = `${environment.apiUrl}/user/v1/${status}/${orgId}`;

		return this.http.get<OrgUsersResponse>(url);
	}

	/**
	 * Get organization registration
	 * @param orgId string
	 */
	getOrganizationRegistration(orgId: string): Observable<OrgRegistration> {
		const url = `${this.apiUrlOrg}/${orgId}`;

		return this.http.get<OrgRegistration>(url);
	}

	/**
	 * Get orgs list
	 * @param orgId string
	 */
	getOrgsList(orgId: string): Observable<OrgListResponse> {
		const url = `${this.apiUrlOrg}/by-user/${orgId}`;

		return this.http.get<OrgListResponse>(url);
	}

	/**
	 * Get partner registration
	 * @param id string
	 */
	getPartner(id: string): Observable<PartnerRegistration> {
		const url = `${this.apiUrlPartner}/${id}`;

		return this.http.get<PartnerRegistration>(url);
	}

	/**
	 * Get partners
	 */
	getPartners(): Observable<PartnerRegistration[]> {
		const url = `${this.apiUrlPartner}`;

		return this.http.get<PartnerRegistration[]>(url);
	}

	/**
	 * Get policies
	 * @param orgId string
	 * @param policyType 'health' | 'dental'
	 */
	getPolicies(
		orgId: string,
		policyType: 'dental' | 'health',
	): Observable<PolicySummary[]> {
		const url = `${this.apiUrlPolicies}/${policyType}/${orgId}`;

		return this.http.get<PolicySummary[]>(url);
	}

	/**
	 * Get policy registration
	 * @param orgId string
	 * @param policyType string = 'health'|'dental'
	 * @param policyId string
	 */
	getPolicy(
		orgId: string,
		policyType: string,
		policyId: string,
	): Observable<PolicyRegistrationResponse> {
		const url = `${this.apiUrlPolicies}/${policyType}/${orgId}/${policyId}`;

		return this.http.get<PolicyRegistrationResponse>(url);
	}

	/**
	 * Get position registration
	 * @param orgId string
	 * @param positionId string
	 */
	getPosition(orgId: string, positionId: string): Observable<unknown> {
		const url = `${this.apiUrlPositions}/${orgId}/${positionId}`;

		return this.http.get<unknown>(url);
	}

	/**
	 * Get positions
	 * @param orgId string
	 */
	getPositions(orgId: string): Observable<PositionSummary[]> {
		const url = `${this.apiUrlPositions}/all/${orgId}`;

		return this.http.get<PositionSummary[]>(url);
	}

	/**
	 * Get products
	 */
	getProducts(): Observable<ProductsSummary[]> {
		const url = `${this.apiUrlProducts}`;

		return this.http.get<ProductsSummary[]>(url);
	}

	/**
	 * Get Populational Scores
	 * @param search string | null
	 */
	getScores(search: string | null): Observable<ScoreResponse> {
		const url = `${environment.apiUrl}/score/v1/all${
			search ? `?search=${search}` : ''
		}`;

		return this.http.get<ScoreResponse>(url);
	}

	/**
	 * Get user registration
	 * @param orgId string
	 * @param userType string
	 * @param userId string
	 */
	getUser(
		orgId: string,
		userType: string,
		userId: string,
	): Observable<UserRegistration> {
		let url = `${environment.apiUrl}/user/v1/by-org/${userId}`;

		if (userType === 'inativo') {
			url = `${environment.apiUrl}/user/v1/${orgId}/inactive/${userId}`;
		}

		return this.http.get<UserRegistration>(url);
	}

	/**
	 * Edit beneficiary
	 * @param orgId string
	 * @param beneficiaryId string
	 * @param request BeneficiaryEditRequest
	 */
	patchBeneficiary(
		orgId: string,
		beneficiaryId: string,
		request: any,
		create: boolean,
	): Observable<MessageResponse> {
		let url: string;

		if (create) {
			url = `${environment.apiUrl}/beneficiary/v1/${orgId}/${beneficiaryId}/update-beneficiary-after-created`;
		} else {
			url = `${environment.apiUrl}/beneficiary/${beneficiaryId}/organizations/${orgId}`;
		}

		return this.http.patch<MessageResponse>(url, request);
	}

	/**
	 * Fetch Beneficiary Benefits
	 * @param beneficiaryId string
	 * @param request any
	 * deprecated
	 */
	patchBeneficiaryBenefits(
		beneficiaryId: string,
		request: any,
	): Observable<MessageResponse> {
		const url = `${environment.apiUrl}/beneficiary/${beneficiaryId}/benefits`;

		return this.http.patch<MessageResponse>(url, request);
	}

	/**
	 * Fetch Beneficiary Policies
	 * @param beneficiaryId string
	 * @param request any
	 * deprecated
	 */
	patchBeneficiaryPolicies(
		beneficiaryId: string,
		request: any,
	): Observable<MessageResponse> {
		const url = `${environment.apiUrl}/beneficiary/policy/${beneficiaryId}`;

		return this.http.patch<MessageResponse>(url, request);
	}

	/**
	 * Edit benefit
	 * @param orgId string
	 * @param benefitId string
	 * @param request BenefitRequest
	 */
	patchBenefit(
		orgId: string,
		benefitId: string,
		request: BenefitRequest,
	): Observable<MessageResponse> {
		const url = `${this.apiUrlBenefits}/${orgId}/${benefitId}`;

		return this.http.patch<MessageResponse>(url, request);
	}

	/**
	 * Edit Dependent Registration
	 * @param dependentId string
	 * @param dto BeneficiaryDependentUpdateDto
	 * @returns
	 */
	patchDependent({
		dependentId,
		dto,
	}: {
		dependentId: string;
		dto: BeneficiaryDependentUpdateDto;
	}): Observable<MessageResponse> {
		const url = `${environment.apiUrl}/beneficiary/dependents/${dependentId}`;

		return this.http.patch<MessageResponse>(url, dto);
	}

	/**
	 * Edit Dependent Benefits
	 * @param depId string
	 * @param request BeneficiaryDependentUpdateDto
	 */
	patchDependentBenefits(
		depId: string,
		request: BeneficiaryDependentUpdateDto,
	): Observable<MessageResponse> {
		const url = `${environment.apiUrl}/beneficiary/${depId}/benefits`;

		return this.http.patch<MessageResponse>(url, request);
	}

	/**
	 * Update Beneficiary registration status/pendencies
	 * @param beneficiaryId string
	 */
	patchOrgBenePendencies(
		beneficiaryId: string,
		request: OrgBeneficiaryPendenciesRequest,
	): Observable<unknown> {
		const url = `${environment.apiUrl}/beneficiary/pendency/${beneficiaryId}`;

		return this.http.patch<unknown>(url, request);
	}

	/**
	 * Edit organization registration
	 * @param orgId string
	 * @param request OrgRegistration
	 */
	patchOrganizationRegistration(
		orgId: string,
		request: NewOrganizationRequest,
	): Observable<MessageResponse> {
		const url = `${this.apiUrlOrg}/${orgId}`;

		return this.http.patch<MessageResponse>(url, request);
	}

	/**
	 * Edit partner
	 * @param partnerId string
	 * @param request PartnerRequest
	 */
	patchPartner(
		partnerId: string,
		request: PartnerRequest,
	): Observable<MessageResponse> {
		const url = `${this.apiUrlPartner}/${partnerId}`;

		return this.http.patch<MessageResponse>(url, request);
	}

	/**
	 * Edit policy
	 * @param orgId string
	 * @param policyId string
	 * @param request PolicyRequest
	 */
	patchPolicy(
		orgId: string,
		policyId: string,
		request: any,
	): Observable<MessageResponse> {
		const url = `${this.apiUrlPolicies}/${orgId}/${policyId}`;

		return this.http.patch<MessageResponse>(url, request);
	}

	/**
	 * Edit position
	 * @param orgId string
	 * @param positionId string
	 * @param request PositionRequest
	 */
	patchPosition(
		orgId: string,
		positionId: string,
		request: PositionRequest,
	): Observable<MessageResponse> {
		const url = `${this.apiUrlPositions}/${orgId}/${positionId}`;

		return this.http.patch<MessageResponse>(url, request);
	}

	/**
	 * Edit beneficiary
	 * @param orgId string
	 * @param beneficiaryId string
	 * @param status string = 'inativo'|'aposentado'
	 * @param cpf string
	 * @param is_deleted boolean
	 */
	patchRetiredInactiveBeneficiary(
		orgId: string,
		beneficiaryId: string,
		status: string,
		cpf: string,
		is_deleted: boolean,
	): Observable<MessageResponse> {
		const type = status === 'inativo' ? 'INACTIVE' : 'RETIRED';
		const url = `${environment.apiUrl}/beneficiary/v1/${orgId}/${beneficiaryId}/update-inactive-or-retired/${type}`;

		return this.http.patch<MessageResponse>(url, { cpf, is_deleted });
	}

	/**
	 * Edit user
	 * @param orgId string
	 * @param userId string
	 * @param request UserEditRequest
	 */
	patchUser(
		orgId: string,
		userId: string,
		request: UserEditRequest,
	): Observable<IdAndMessageResponse2> {
		const url = `${environment.apiUrl}/user/${userId}/organizations/${orgId}`;

		return this.http.patch<IdAndMessageResponse2>(url, request);
	}

	/**
	 * Post Beneficiary Files
	 * @param orgId string
	 * @param beneficiaryId string
	 * @param request any
	 * @param inactive boolean
	 */
	postBeneficiaryFiles(
		orgId: string,
		beneficiaryId: string,
		inactive: boolean,
		request: any,
	): Observable<MessageResponse> {
		let url = `${environment.apiUrl}/beneficiary/archives/${orgId}/${beneficiaryId}`;

		if (inactive) {
			url = `${environment.apiUrl}/beneficiary/v1/${beneficiaryId}/files/disconnected/${orgId}`;
		}

		return this.http.post<MessageResponse>(url, request);
	}

	/**
	 * Edit beneficiary type (inactive/retired)
	 * @param orgId string
	 * @param beneficiaryType string
	 * @param beneficiaryId string
	 * @param cpf string
	 */
	postBeneficiaryType(
		orgId: string,
		beneficiaryType: string,
		beneficiaryId: string,
		cpf: string,
	): Observable<MessageResponse> {
		const url = `${environment.apiUrl}/beneficiary/v1/${orgId}/${beneficiaryId}/retired-and-inactive`;

		return this.http.post<MessageResponse>(url, { cpf });
	}

	/**
	 * Create benefit
	 * @param orgId string
	 * @param request BenefitRequest
	 */
	postBenefit(
		orgId: string,
		request: BenefitRequest,
	): Observable<BenefitRequest> {
		const url = `${this.apiUrlBenefits}/${orgId}`;

		return this.http.post<BenefitRequest>(url, request);
	}

	/**
	 * Post Bulk action file
	 * @param actionType OrgActionType
	 * @param request any
	 */
	postBulkActionFile(
		actionType: TemplateFileType,
		request: any,
	): Observable<MessageResponse> {
		const url = `${environment.apiUrl}/beneficiary/bulk`;

		switch (actionType) {
			case TemplateFileType.INCLUSION:
				return this.http.post<MessageResponse>(url, request);
			case TemplateFileType.UPDATE:
				return this.http.patch<MessageResponse>(url, request);
			case TemplateFileType.EXCLUSION:
				return this.http.request<MessageResponse>('delete', url, {
					body: request,
				});
			case TemplateFileType.TRANSFER: {
				const transferUrl = `${url}/transfer`;

				return this.http.post<MessageResponse>(transferUrl, request);
			}

			default: {
				throw new Error('Caso não tratado 2'); // TODO: Tratar
			}
		}
	}

	/**
	 * Add Dependent
	 * @param orgId string
	 * @param cpf string
	 * @param request BeneficiaryDependentUpdateDto
	 */
	postDependent(
		orgId: string,
		cpf: string,
		request: BeneficiaryDependentUpdateDto,
	): Observable<MessageResponse> {
		const url = `${environment.apiUrl}/beneficiary/v1/create/dependent/${orgId}`;

		return this.http.post<MessageResponse>(url, request);
	}

	/**
	 * Edit Dependent Benefits
	 * @param depId string
	 * @param request BeneficiaryDependentUpdateDto
	 */
	postEditBeneStatus(
		request: EditBeneStatusRequest,
	): Observable<EditBeneStatusResponse> {
		const url = `${environment.apiUrl}/beneficiary/v1/update-status-beneficiary`;

		return this.http.post<EditBeneStatusResponse>(url, request);
	}

	/**
	 * Create new organization
	 * @param request NewOrganizationRequest
	 */
	postOrganizations(
		request: NewOrganizationRequest,
	): Observable<MessageResponse> {
		const url = `${this.apiUrlOrg}`;

		return this.http.post<MessageResponse>(url, request);
	}

	/**
	 * Create partner
	 * @param request PartnerRequest
	 */
	postPartner(request: PartnerRequest): Observable<MessageResponse> {
		const url = `${this.apiUrlPartner}`;

		return this.http.post<MessageResponse>(url, request);
	}

	/**
	 * Create policy
	 * @param orgId string
	 * @param request PolicyRequest
	 */
	postPolicy(orgId: string, request: PolicyRequest): Observable<PolicyRequest> {
		const url = `${this.apiUrlPolicies}/${orgId}`;

		return this.http.post<PolicyRequest>(url, request);
	}

	/**
	 * Create position
	 * @param orgId string
	 * @param request PositionRequest
	 */
	postPosition(
		orgId: string,
		request: PositionRequest,
	): Observable<PositionRequest> {
		const url = `${this.apiUrlPositions}/${orgId}`;

		return this.http.post<PositionRequest>(url, request);
	}

	/**
	 * Clone position
	 * @param orgId string
	 * @param request { id: string }
	 */
	postPositionClone(
		orgId: string,
		request: { id: string },
	): Observable<{ id: string }> {
		const url = `${this.apiUrlPositions}/copy/${orgId}`;

		return this.http.post<{ id: string }>(url, request);
	}

	/**
	 * Fetch Populational Scores XLSX URL
	 * @param search string | null
	 */
	postScoresXLSXUrl(): Observable<URLResponse> {
		const url = `${environment.apiUrl}/score/v1/generate/excel`;

		return this.http.post<URLResponse>(url, {});
	}

	/**
	 * Create user
	 * @param orgId string
	 * @param request UserRequest
	 */
	postUser(
		orgId: string,
		request: UserRequest,
	): Observable<IdAndMessageResponse2> {
		const url = `${environment.apiUrl}/user/v1/register/${orgId}`;

		return this.http.post<IdAndMessageResponse2>(url, request);
	}

	/**
	 * Post Dashboard chart URL
	 * @param request DashChartInfo
	 * @param linkId string?
	 */
	saveDashChartUrl(
		request: DashChartInfo,
		linkId?: string,
	): Observable<MessageResponse> {
		const url = `${environment.apiUrl}/dashboard/v1/${
			linkId ? `update-link/${linkId}` : 'create-link'
		}`;

		if (linkId) {
			return this.http.put<MessageResponse>(url, request);
		}

		return this.http.post<MessageResponse>(url, request);
	}

	showSnack(info: string, type: string) {
		this._snackBar.openFromComponent(SnackBarComponent, {
			data: { info, type },
		});
	}

	/**
	 * Toggle favorite organization
	 * @param organizationId string
	 */
	toggleFavoriteOrganization(
		organizationId: string,
	): Observable<MessageResponse> {
		const url = `${environment.apiUrl}/user/v1/toggle-favorite`;

		return this.http.post<MessageResponse>(url, { organizationId });
	}
}
